import {
  CompareValues,
  NicotineUse,
  PaymentType,
  PlanCode,
  Sex,
  StateCode,
  TermLength,
  ToolkitCode,
} from '@itk/types';
import { CheatSheetItem, HiQuote } from './quoter.model';

export function isQuoterDataWellFormed<T>(x?: unknown | null): x is T {
  if (x === null || x === undefined) return false;

  if (typeof x === 'object') {
    if (!('form' in x)) return false;
    if (!('quotes' in x)) return false;
    if (!('excludedQuotes' in x)) return false;
    if (!('questionnaires' in x)) return false;
    if (!Array.isArray(x['quotes'])) return false;
    if (!Array.isArray(x['excludedQuotes'])) return false;
    if (typeof x['questionnaires'] !== 'object') return false;
  } else {
    return false;
  }

  return true;
}

/* Lite Quoter Request */
export type FexLiteQuoteRequest = FexQuoteRequestOptions &
  BaseRequestOptions &
  LiteRequestOptions &
  QuoteClientDataOptions;

export type IULLiteQuoteRequest = FexLiteQuoteRequest

export type TermLiteQuoteRequest = TermQuoteRequestOptions &
  BaseRequestOptions &
  LiteRequestOptions &
  QuoteClientDataOptions;

export type MedSuppLiteQuoteRequest = MedSuppQuoteRequestOptions &
  BaseRequestOptions &
  LiteRequestOptions &
  QuoteClientDataOptions;

/* Full Quoter Request */
export type TermFullQuoteRequest = TermQuoteRequestOptions &
  BaseRequestOptions &
  FullRequestOptions &
  HeightWeightRequestOptions &
  QuoteClientDataOptions;

export type FexFullQuoteRequest = FexQuoteRequestOptions &
  BaseRequestOptions &
  FullRequestOptions &
  HeightWeightRequestOptions &
  QuoteClientDataOptions;

export type IULFullQuoteRequest = FexFullQuoteRequest

export type MedSuppFullQuoteRequest = MedSuppQuoteRequestOptions &
  BaseRequestOptions &
  FullRequestOptions &
  HeightWeightRequestOptions &
  QuoteClientDataOptions;

export type FexTermMedsuppUnifiedFullRequest = TermFullQuoteRequest &
  FexFullQuoteRequest &
  MedSuppFullQuoteRequest;

export type DvhFullQuoteRequest = DvhQuoteRequestOptions &
  QuoteClientDataOptions;

export type HiFullQuoteRequest = HiQuoteRequestOptions &
  Omit<QuoteClientDataOptions, 'age'>;

/* Compare Request */
export type FexCompareQuoteRequest = FexQuoteRequestOptions &
  BaseRequestOptions &
  QuoteClientDataOptions &
  CompareQuoteRequestOptions;

export type IULCompareQuoteRequest = FexCompareQuoteRequest;

export type TermCompareQuoteRequest = TermQuoteRequestOptions &
  BaseRequestOptions &
  QuoteClientDataOptions &
  CompareQuoteRequestOptions;
export type MedSuppCompareQuoteRequest = MedSuppQuoteRequestOptions &
  BaseRequestOptions &
  QuoteClientDataOptions &
  CompareQuoteRequestOptions;
export type DvhCompareQuoteRequest = DvhQuoteRequestOptions &
  DvhCompaniesOptions &
  QuoteClientDataOptions;

/* Cheat Sheet Request */
export interface CheatSheetRequest {
  item: Omit<CheatSheetItem, 'info'>;
  state: StateCode;
  toolkit: ToolkitCode;
}

export interface CompaniesRequest {
  state: string;
  toolkit: ToolkitCode;
}

interface DvhQuoteRequestOptions {
  covered_individuals?: string | null;
  calendar_year_benefit?: string | null;
  deductible?: string | null;
  plan_name?: string | null;
  state?: StateCode | null;
  toolkit?: ToolkitCode;
  zipcode?: string | null;
}

interface HiQuoteRequestOptions {
  hideRiders?: HiQuote['hide_riders'];
  state?: StateCode | null;
  sex?: Sex | null;
  toolkit?: ToolkitCode;
  days?: number | null;
  dailyBenefit?: number | null;
  planName?: string | null;
  carriersFilter?: string[] | null;
  riders?:
    | {
        name: string;
        value: string | number | null;
      }[]
    | null;
}

interface DvhCompaniesOptions {
  carriers_filter: string[];
}

interface FexQuoteRequestOptions {
  coverageType?: string | null;
  faceAmount?: number | string | null;
  premiumAmount?: number | string | null;
  paymentType?: PaymentType | null;
}

interface CompareQuoteRequestOptions {
  coverageType?: string | null;
  type?: 'MIXED';
  values?: CompareValues;
  company?: string;
}

interface TermQuoteRequestOptions {
  returnOfPremium?: boolean | null;
  term?: TermLength | null;
  faceAmount?: number | string | null;
  premiumAmount?: number | string | null;
}

interface MedSuppQuoteRequestOptions {
  hhdToggle?: boolean | null;
  countyName?: string | null;
  zipcode?: string | null;
  effectiveDate?: string | null;
  planCode?: PlanCode | null;
}

interface BaseRequestOptions {
  sex?: Sex | null;
  tobacco?: NicotineUse | null;
  state?: StateCode | null;
  toolkit?: ToolkitCode | null;
}

interface HeightWeightRequestOptions {
  feet?: number | string | null;
  inches?: number | string | null;
  weight?: number | string | null;
}

interface LiteRequestOptions {
  token?: string | null;
}

interface FullRequestOptions {
  // TODO(elijah): add underwriting data type
  underwritingItems?: unknown[] | null;
}

interface QuoteClientDataOptions {
  month?: number | string | null;
  day?: number | string | null;
  year?: number | string | null;
  age?: number | string | null;
}
